import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, LogOut, Trash2, Download, 
  BarChart3, Settings, Users, CreditCard,
  TrendingUp, DollarSign, Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('leads');

  // Analytics State
  const [gaTrackingId, setGaTrackingId] = useState('');
  const [googleAdsId, setGoogleAdsId] = useState('');
  const [metaPixelId, setMetaPixelId] = useState('');

  // Stripe State
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [stripeWebhook, setStripeWebhook] = useState('');
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('edx_admin_token');
    if (!token) {
      navigate('/edx-admin');
      return;
    }

    fetchUser();
    fetchLeads();
    loadSettings();
  }, [navigate]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter]);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('edx_admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      handleLogout();
    }
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('edx_admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = () => {
    // Load from localStorage
    setGaTrackingId(localStorage.getItem('edx_ga_tracking_id') || '');
    setGoogleAdsId(localStorage.getItem('edx_google_ads_id') || '');
    setMetaPixelId(localStorage.getItem('edx_meta_pixel_id') || '');
    setStripePublicKey(localStorage.getItem('edx_stripe_public_key') || '');
    setStripeWebhook(localStorage.getItem('edx_stripe_webhook') || '');
    setCheckoutEnabled(localStorage.getItem('edx_checkout_enabled') === 'true');
  };

  const saveAnalyticsSettings = () => {
    localStorage.setItem('edx_ga_tracking_id', gaTrackingId);
    localStorage.setItem('edx_google_ads_id', googleAdsId);
    localStorage.setItem('edx_meta_pixel_id', metaPixelId);
    toast.success('Analytics settings saved!');
  };

  const savePaymentSettings = () => {
    localStorage.setItem('edx_stripe_public_key', stripePublicKey);
    localStorage.setItem('edx_stripe_webhook', stripeWebhook);
    localStorage.setItem('edx_checkout_enabled', checkoutEnabled.toString());
    toast.success('Payment settings saved!');
  };

  const filterLeads = () => {
    let filtered = leads;

    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.phone.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    setFilteredLeads(filtered);
  };

  const handleStatusChange = async (email, newStatus) => {
    try {
      const token = localStorage.getItem('edx_admin_token');
      await axios.put(
        `${BACKEND_URL}/api/leads/${email}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      const token = localStorage.getItem('edx_admin_token');
      await axios.delete(`${BACKEND_URL}/api/leads/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('edx_admin_token');
    navigate('/edx-admin');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Service', 'Appointment', 'Status', 'Submitted At'];
    const rows = filteredLeads.map((lead) => [
      lead.name,
      lead.phone,
      lead.email,
      lead.service,
      lead.appointment_datetime || 'N/A',
      lead.status,
      new Date(lead.submitted_at).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `edx-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[hsl(210,40%,98%)] flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  return (
    <div className="min-h-screen bg-[hsl(210,40%,98%)]" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[hsl(215,75%,16%)] to-[hsl(224,64%,33%)] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
                <span className="text-[hsl(215,75%,16%)] font-bold text-2xl">EDX</span>
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                  EDX Control Panel
                </h1>
                {user && <p className="text-sm text-white/80">Welcome, {user.username}</p>}
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-md">
            <TabsTrigger value="overview" className="rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Leads
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg">
              <Activity className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-[hsl(215,75%,16%)]">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Leads</h3>
                  <Users className="w-8 h-8 text-[hsl(215,75%,16%)]" />
                </div>
                <p className="text-4xl font-black text-[hsl(215,75%,16%)]">{stats.total}</p>
                <p className="text-xs text-gray-500 mt-2">All time</p>
              </div>
              
              <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase">New Leads</h3>
                  <TrendingUp className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-4xl font-black text-blue-500">{stats.new}</p>
                <p className="text-xs text-gray-500 mt-2">Pending contact</p>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-yellow-500">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase">Contacted</h3>
                  <Activity className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-4xl font-black text-yellow-500">{stats.contacted}</p>
                <p className="text-xs text-gray-500 mt-2">In progress</p>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase">Converted</h3>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-4xl font-black text-green-500">{stats.converted}</p>
                <p className="text-xs text-gray-500 mt-2">Closed deals</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-md p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-6" style={{ fontFamily: 'Outfit' }}>
                Quick Overview
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-700">Conversion Rate</span>
                  <span className="text-2xl font-bold text-[hsl(224,64%,33%)]">
                    {stats.total > 0 ? ((stats.converted / stats.total) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-700">Response Rate</span>
                  <span className="text-2xl font-bold text-[hsl(224,64%,33%)]">
                    {stats.total > 0 ? (((stats.contacted + stats.converted) / stats.total) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, or phone"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="admin-search-input"
                  />
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="admin-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  onClick={exportToCSV}
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                  data-testid="admin-export-button"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </Button>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Appointment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                          No leads found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLeads.map((lead, index) => (
                        <TableRow key={index} data-testid={`lead-row-${index}`}>
                          <TableCell className="font-medium">{lead.name}</TableCell>
                          <TableCell>
                            <a href={`tel:${lead.phone}`} className="text-[hsl(224,64%,33%)] hover:underline">
                              {lead.phone}
                            </a>
                          </TableCell>
                          <TableCell>
                            <a href={`mailto:${lead.email}`} className="text-[hsl(224,64%,33%)] hover:underline">
                              {lead.email}
                            </a>
                          </TableCell>
                          <TableCell>{lead.service}</TableCell>
                          <TableCell>
                            {lead.appointment_datetime
                              ? new Date(lead.appointment_datetime).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={lead.status}
                              onValueChange={(value) => handleStatusChange(lead.email, value)}
                            >
                              <SelectTrigger className="w-32" data-testid={`lead-status-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>{new Date(lead.submitted_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(lead.email)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`lead-delete-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-6" style={{ fontFamily: 'Outfit' }}>
                Analytics & Tracking Integration
              </h2>
              <p className="text-gray-600 mb-8">
                Configure your tracking IDs for Google Analytics, Google Ads, and Meta Pixel to monitor website performance and conversions.
              </p>

              <div className="space-y-6">
                {/* Google Analytics */}
                <div>
                  <Label className="text-base font-semibold mb-2">Google Analytics 4 (GA4)</Label>
                  <p className="text-sm text-gray-600 mb-3">Measurement ID (e.g., G-XXXXXXXXXX)</p>
                  <Input
                    value={gaTrackingId}
                    onChange={(e) => setGaTrackingId(e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                    className="max-w-md"
                  />
                </div>

                {/* Google Ads */}
                <div>
                  <Label className="text-base font-semibold mb-2">Google Ads</Label>
                  <p className="text-sm text-gray-600 mb-3">Conversion Tracking ID (e.g., AW-XXXXXXXXXX)</p>
                  <Input
                    value={googleAdsId}
                    onChange={(e) => setGoogleAdsId(e.target.value)}
                    placeholder="AW-XXXXXXXXXX"
                    className="max-w-md"
                  />
                </div>

                {/* Meta Pixel */}
                <div>
                  <Label className="text-base font-semibold mb-2">Meta Pixel (Facebook/Instagram)</Label>
                  <p className="text-sm text-gray-600 mb-3">Pixel ID (numeric only)</p>
                  <Input
                    value={metaPixelId}
                    onChange={(e) => setMetaPixelId(e.target.value)}
                    placeholder="123456789012345"
                    className="max-w-md"
                  />
                </div>

                <Button
                  onClick={saveAnalyticsSettings}
                  className="btn-premium btn-premium-primary"
                >
                  Save Analytics Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-6" style={{ fontFamily: 'Outfit' }}>
                Payment & Checkout Integration
              </h2>
              <p className="text-gray-600 mb-8">
                Configure Stripe for secure payment processing on your shop.
              </p>

              <div className="space-y-6">
                {/* Stripe Public Key */}
                <div>
                  <Label className="text-base font-semibold mb-2">Stripe Publishable Key</Label>
                  <p className="text-sm text-gray-600 mb-3">Starts with pk_live_ or pk_test_</p>
                  <Input
                    value={stripePublicKey}
                    onChange={(e) => setStripePublicKey(e.target.value)}
                    placeholder="pk_live_xxxxxxxxxxxxxxxx"
                    className="max-w-lg"
                    type="password"
                  />
                </div>

                {/* Webhook URL */}
                <div>
                  <Label className="text-base font-semibold mb-2">Webhook URL</Label>
                  <p className="text-sm text-gray-600 mb-3">Your backend webhook endpoint</p>
                  <Input
                    value={stripeWebhook}
                    onChange={(e) => setStripeWebhook(e.target.value)}
                    placeholder="https://yourdomain.com/api/webhook/stripe"
                    className="max-w-lg"
                  />
                </div>

                {/* Enable Checkout */}
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={checkoutEnabled}
                    onChange={(e) => setCheckoutEnabled(e.target.checked)}
                    className="w-5 h-5 text-[hsl(224,64%,33%)] rounded"
                    id="checkout-enabled"
                  />
                  <Label htmlFor="checkout-enabled" className="text-base font-semibold cursor-pointer">
                    Enable Checkout on Website
                  </Label>
                </div>

                <Button
                  onClick={savePaymentSettings}
                  className="btn-premium btn-premium-primary"
                >
                  Save Payment Settings
                </Button>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> To complete Stripe integration, you'll need to add your Secret Key to the backend environment variables.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-6" style={{ fontFamily: 'Outfit' }}>
                General Settings
              </h2>
              <p className="text-gray-600">
                Additional configuration options will be available here.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
